import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../lib/api";
import { previewPdfResponse } from "../../lib/pdfPreview";
import { FileText, Eye, Heart, Share2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("de-DE", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const EVENT_LABELS = {
    listing_created: "Erfasst (Eingestellt)",
    listing_activated: "Aktiviert / Veröffentlicht",
    listing_paused: "Pausiert",
    listing_sold: "Verkauft / Reserviert",
    listing_deleted: "Gelöscht",
    listing_duplicated: "Kopiert",
    listing_updated: "Aktualisiert",
    listing_view: "Aufruf",
    listing_favorited: "In Favoriten gespeichert",
    listing_referral_share: "Weiterempfehlung (Transaktions­code)",
    listing_preview_viewed: "Vorschau angesehen",
    listing_published: "Veröffentlicht",
    bid_placed: "Gebot abgegeben",
    price_offer_made: "Preisvorschlag eingegangen",
    price_offer_accepted: "Preisvorschlag akzeptiert",
    price_offer_rejected: "Preisvorschlag abgelehnt",
    // Disposition / Abwicklung
    dispo_customer_update: "Kundendaten geändert",
    dispo_items_update: "Zubehör / Positionen geändert",
    dispo_payment_update: "Zahlung geändert",
    dispo_tradein_save: "Inzahlungnahme gespeichert",
    dispo_reserve: "Fahrzeug reserviert",
    dispo_cancel_contract: "Reservierung storniert",
    dispo_invoice_created: "Rechnung erstellt",
    dispo_postage_created: "Porto-Label erstellt / Papiere versendet (Deutsche Post)",
    dispo_papers_shipment: "Papiere versendet",
    dispo_document_uploaded: "Dokument hochgeladen",
    dispo_papers_shipped: "Papiere versendet",
    dispo_transport_ordered: "Transport beauftragt",
    dispo_contract_send: "Vertrag versendet",
    dispo_contract_signed_mock: "Vertrag signiert",
    dispo_location_set: "Lagerort gesetzt",
    dispo_internal_note: "Interner Vermerk geändert",
    dispo_financials_save: "Kalkulation gespeichert",
    dispo_checklist_save: "Checkliste gespeichert",
    dispo_workshop_order_create: "Werkstattauftrag erstellt",
    dispo_handover_save: "Übergabe dokumentiert",
};

// Bereiche, die in einem `listing_updated`-Event über `payload.section` gemeldet werden können
const SECTION_LABELS = {
    pricing: "Festpreise",
    auction: "Auktion",
    sharing: "Sharing / Vermietung",
    master_data: "Fahrzeug-Stammdaten",
    equipment: "Ausstattung",
    accessories: "Zubehör",
    description: "Beschreibung",
    service: "Service & Wartung",
    media: "Bilder & Video",
    marketing: "Werbung & Reichweite",
    disposition: "Disposition & Verkaufsabwicklung",
    preview: "Vorschau",
    publish: "Veröffentlichung",
    lifecycle: "Lebenszyklus",
    engagement: "Reichweite",
    offers: "Preisvorschläge / Gebote",
};

// Bereiche, die im Filter-Dropdown wählbar sind (UI + PDF).
const FILTER_SECTIONS = [
    "pricing", "auction", "sharing", "master_data", "equipment", "accessories",
    "description", "service", "media", "marketing", "disposition", "lifecycle",
    "engagement", "offers",
];

// Leitet den Bereich eines Events ab (Spiegel von backend `_event_section`).
const eventSection = (ev) => {
    const p = ev.payload || {};
    if (p.section) return p.section;
    const et = ev.event_type || "";
    if (et.startsWith("dispo_")) return "disposition";
    if (["listing_created", "listing_activated", "listing_paused", "listing_sold",
        "listing_deleted", "listing_duplicated", "listing_published"].includes(et)) return "lifecycle";
    if (["listing_view", "listing_favorited", "listing_referral_share", "listing_preview_viewed"].includes(et)) return "engagement";
    if (["bid_placed", "price_offer_made", "price_offer_accepted", "price_offer_rejected"].includes(et)) return "offers";
    return null;
};

/**
 * ListingProtocol — collapsible audit-trail + reach statistics for the listing
 * owner. Mounted at the very bottom of the edit form.
 *
 * Data source: GET /api/listings/:id/protocol (owner-only).
 */
export default function ListingProtocol({ listingId }) {
    const [expanded, setExpanded] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filterSection, setFilterSection] = useState("");   // "" = alle Bereiche
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/listings/${listingId}/protocol`);
            setData(r.data);
        } catch (e) {
            console.error("Failed to load listing protocol", e);
        } finally {
            setLoading(false);
        }
    }, [listingId]);

    useEffect(() => {
        if (expanded && !data && listingId) load();
    }, [expanded, data, listingId, load]);

    // Clientseitige Filterung der angezeigten Ereignisse (Bereich + Zeitraum).
    const filteredEvents = useMemo(() => {
        const evs = data?.events || [];
        return evs.filter((ev) => {
            const day = (ev.created_at || "").slice(0, 10);
            if (dateFrom && day && day < dateFrom) return false;
            if (dateTo && day && day > dateTo) return false;
            if (filterSection && eventSection(ev) !== filterSection) return false;
            return true;
        });
    }, [data, filterSection, dateFrom, dateTo]);

    const exportPdf = useCallback(async () => {
        setExporting(true);
        try {
            const params = {};
            if (filterSection) params.sections = filterSection;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const r = await api.get(`/listings/${listingId}/protocol.pdf`, { params, responseType: "blob" });
            previewPdfResponse(r, `Protokoll_${listingId}.pdf`);
        } catch (e) {
            console.error("PDF-Export fehlgeschlagen", e);
            toast.error("Protokoll-PDF konnte nicht erstellt werden");
        } finally {
            setExporting(false);
        }
    }, [listingId, filterSection, dateFrom, dateTo]);

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="listing-protocol">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-[#FFFBEB]/40 text-left"
                data-testid="listing-protocol-toggle"
                aria-expanded={expanded}
            >
                <FileText className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-bold">Protokoll</h2>
                    <p className="text-xs text-gray-600 mt-0.5">Aufrufe (B2C/B2B), Favoriten, Weiterempfehlungen &amp; Lebenszyklus-Ereignisse dieses Inserats.</p>
                </div>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>
            {expanded && (
                <div className="p-6 space-y-5" data-testid="listing-protocol-body">
                    {loading && !data && <div className="text-sm text-gray-500">Lade Protokoll …</div>}
                    {data && (
                        <>
                            {/* Counters */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                <Stat icon={Eye} label="Aufrufe B2C" value={data.views_b2c || 0} testId="protocol-views-b2c" />
                                <Stat icon={Eye} label="Aufrufe B2B" value={data.views_b2b || 0} testId="protocol-views-b2b" />
                                <Stat icon={Heart} label="Gemerkt" value={data.favorites_count || 0} testId="protocol-favs" />
                                <Stat icon={Share2} label="Weiterempfehlungen" value={data.referrals_count || 0} testId="protocol-referrals" />
                            </div>

                            {/* Key timestamps */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm bg-gray-50 border border-gray-200 rounded-sm p-4" data-testid="protocol-key-dates">
                                <KV k="Eingestellt am" v={fmtDate(data.created_at)} />
                                <KV k="Zuletzt geändert" v={fmtDate(data.updated_at)} />
                                <KV k="Aktiviert am" v={fmtDate(data.activated_at)} />
                                <KV k="Pausiert am" v={fmtDate(data.paused_at)} />
                                <KV k="Verkauft am" v={fmtDate(data.sold_at)} />
                                <KV k="Verkauft an" v={data.sold_to_label || data.sold_to_user_id || "—"} />
                                <KV k="Gelöscht am" v={fmtDate(data.deleted_at)} />
                                <KV k="Aktueller Status" v={data.status || "active"} />
                            </div>

                            {/* Filter & PDF-Export */}
                            <div className="flex flex-wrap items-end gap-3 bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="protocol-filter-bar">
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Bereich</label>
                                    <select
                                        value={filterSection}
                                        onChange={(e) => setFilterSection(e.target.value)}
                                        className="h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white min-w-[180px]"
                                        data-testid="protocol-filter-section"
                                    >
                                        <option value="">Alle Bereiche</option>
                                        {FILTER_SECTIONS.map((s) => (
                                            <option key={s} value={s}>{SECTION_LABELS[s] || s}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Von</label>
                                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white" data-testid="protocol-filter-from" />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Bis</label>
                                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white" data-testid="protocol-filter-to" />
                                </div>
                                {(filterSection || dateFrom || dateTo) && (
                                    <button
                                        type="button"
                                        onClick={() => { setFilterSection(""); setDateFrom(""); setDateTo(""); }}
                                        className="h-9 px-3 text-xs font-bold text-gray-600 border border-gray-300 rounded-sm bg-white hover:bg-gray-100"
                                        data-testid="protocol-filter-reset"
                                    >
                                        Zurücksetzen
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={exportPdf}
                                    disabled={exporting}
                                    className="h-9 ml-auto inline-flex items-center gap-1.5 px-4 text-xs font-bold uppercase tracking-wider bg-[#111827] text-white rounded-sm hover:bg-black disabled:opacity-60"
                                    data-testid="protocol-export-pdf"
                                >
                                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                    {exporting ? "Erstelle PDF…" : "Als PDF exportieren"}
                                </button>
                            </div>

                            {/* Full event log */}
                            <div data-testid="protocol-events-list">
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Ereignisse ({filteredEvents.length})</div>
                                {filteredEvents.length > 0 ? (
                                    <ol className="space-y-2 max-h-80 overflow-y-auto pr-2">
                                        {filteredEvents.map((ev, i) => {
                                            const sectionLabel = ev.payload?.section && SECTION_LABELS[ev.payload.section];
                                            const changes = Array.isArray(ev.payload?.changes) ? ev.payload.changes : null;
                                            return (
                                                <li key={ev.id || i} className="flex items-start gap-3 border-l-2 border-[#FFCC00] pl-3 py-1" data-testid={`protocol-event-${i}`}>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-bold text-sm">
                                                            {EVENT_LABELS[ev.event_type] || ev.event_type}
                                                            {sectionLabel && <span className="ml-2 inline-block text-[10px] uppercase tracking-wider bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-sm">{sectionLabel}</span>}
                                                            {ev.payload?.tx_code && <span className="ml-2 inline-block text-[10px] uppercase tracking-wider bg-[#FFFBEB] text-[#92400E] px-1.5 py-0.5 rounded-sm font-mono-num">TX {ev.payload.tx_code}</span>}
                                                        </div>
                                                        {changes && changes.length > 0 && (
                                                            <ul className="mt-1 space-y-0.5" data-testid={`protocol-event-changes-${i}`}>
                                                                {changes.map((c, ci) => (
                                                                    <li key={ci} className="text-[11px] text-gray-600 flex flex-wrap items-center gap-1">
                                                                        <span className="font-bold text-gray-700">{c.label}:</span>
                                                                        <span className="line-through text-gray-400 break-all">{c.old}</span>
                                                                        <span className="text-gray-400">→</span>
                                                                        <span className="font-bold text-emerald-700 break-all">{c.new}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        <div className="text-[11px] text-gray-500 mt-0.5">
                                                            {fmtDate(ev.created_at)} · {ev.actor_label || ev.actor_id || "—"}
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ol>
                                ) : (
                                    <div className="text-xs text-gray-500 italic" data-testid="protocol-events-empty">{(data.events || []).length > 0 ? "Keine Ereignisse im gewählten Filter." : "Noch keine Ereignisse aufgezeichnet."}</div>
                                )}
                            </div>

                            <p className="text-[11px] text-gray-500 italic">
                                Bei Löschung wird das Protokoll zur Abrechnung 2 Jahre lang aufbewahrt.
                            </p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

const Stat = ({ icon: Icon, label, value, testId }) => (
    <div className="bg-white border border-gray-200 rounded-sm p-4 flex items-center gap-3" data-testid={testId}>
        <div className="w-10 h-10 bg-[#FFCC00] flex items-center justify-center rounded-sm">
            <Icon className="w-5 h-5 text-[#111827]" strokeWidth={2.4} />
        </div>
        <div>
            <div className="font-display font-black text-xl font-mono-num">{value}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
        </div>
    </div>
);

const KV = ({ k, v }) => (
    <div className="flex justify-between gap-3 py-1">
        <span className="text-gray-500">{k}</span>
        <span className="font-mono-num text-right">{v}</span>
    </div>
);
